import React, { forwardRef } from "react";

import { HashLink } from "react-router-hash-link";
import PropTypes from "prop-types";
import { Link as RouterLink } from "react-router-dom";
import { inServer } from "core-web/constants";
import styled from "theme/libs/styled";

const InlineDiv = styled("div")`
    display: inline;
`;

// isvolatile : If true, don't touch this, let it be.
const Link = forwardRef(({ to, ...parentProps }, ref) => {
    const { isvolatile } = parentProps;

    if (!to && !isvolatile) {
        return parentProps.children;
    }

    let LinkRenderer = RouterLink;
    const type = typeof to;
    let decodedTo = to;

    const scrollWithOffset = (el, behavior) => {
        if (!inServer) {
            const y = el.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({ top: y, behavior });
        }
    };

    if (to && type !== "function") {
        // Check if it's an external link.
        const pathname = type === "object" ? to.pathname : to;

        // Replace amp to handle navigation with filter params
        const decodedPath = pathname?.replace(/&amp;/g, "&");
        decodedTo = type === "object" ? { ...to, pathname: decodedPath } : decodedPath;

        if (pathname && pathname.indexOf("/") !== 0) {
            // Probably not an internal link.
            LinkRenderer = ({ target, rel, ...props }) => {
                // For anchor links
                if (pathname.indexOf("#") === 0) {
                    return (
                        <HashLink
                            {...props}
                            ref={ref}
                            to={(decodedTo && decodedTo) || "/#"}
                            scroll={(el) => scrollWithOffset(el, "smooth")}
                        />
                    );
                }
                return (
                    <a href={pathname} target={target || "_blank"} rel={rel || "noopener"} {...props}>
                        {props.children}
                    </a>
                );
            };
        } else if (pathname && pathname.indexOf("#") !== -1) {
            return (
                <HashLink
                    {...parentProps}
                    ref={ref}
                    to={(decodedTo && decodedTo) || "/#"}
                    scroll={(el) => scrollWithOffset(el, "auto")}
                />
            );
        }
    } else if (isvolatile && type !== "function") {
        return <InlineDiv {...parentProps} ref={ref} />;
    }

    return <LinkRenderer {...parentProps} ref={ref} to={(decodedTo && decodedTo) || "/#"} />;
});

Link.propTypes = {
    children: PropTypes.node,
    rel: PropTypes.string,
    target: PropTypes.string,
    to: PropTypes.oneOfType([PropTypes.object, PropTypes.string, PropTypes.func, PropTypes.bool]),
};

Link.defaultProps = {
    children: null,
    rel: null,
    target: null,
    to: false,
};

export default Link;
